import { AlertTriangle, Mic, Radio, Users } from "lucide-react";

const sessions = [
  { host: "Reza", platform: "TikTok", status: "live" as const, viewers: "2.4k", cta: "32s ago" },
  { host: "Sinta", platform: "Shopee", status: "warning" as const, viewers: "1.1k", cta: "94s ago" },
  { host: "Nadia", platform: "TikTok", status: "live" as const, viewers: "3.8k", cta: "12s ago" },
  { host: "Adi", platform: "Tokopedia", status: "live" as const, viewers: "890", cta: "45s ago" },
];

const sessionStatusStyle = {
  live: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
};

const sessionDot = {
  live: "bg-success",
  warning: "bg-warning",
};

const alerts = [
  {
    type: "critical" as const,
    title: "No CTA detected",
    desc: "No call-to-action in the last 90 seconds.",
    host: "Sinta",
    time: "14:05:40",
  },
  {
    type: "warning" as const,
    title: "Host inactivity",
    desc: "Speech activity dropped significantly.",
    host: "Adi",
    time: "14:06:12",
  },
  {
    type: "resolved" as const,
    title: "CTA recovered",
    desc: "Host resumed strong CTA behavior.",
    host: "Nadia",
    time: "14:06:55",
  },
];

const dotColor = {
  critical: "bg-destructive",
  warning: "bg-warning",
  resolved: "bg-success",
};

const badgeStyle = {
  critical: "bg-destructive/15 text-destructive",
  warning: "bg-warning/15 text-warning",
  resolved: "bg-success/15 text-success",
};

export function HostIntelligenceMockup() {
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card/30">
      {/* Stats row */}
      <div className="grid grid-cols-4 border-b border-border">
        {[
          { label: "LIVE SESSIONS", value: "6", icon: Radio },
          { label: "ACTIVE HOSTS", value: "4", icon: Users },
          { label: "ALERTS", value: "3", icon: AlertTriangle },
          { label: "AVG CTA RATE", value: "Every 48s", icon: Mic },
        ].map((stat, i) => (
          <div key={i} className={`p-4 ${i < 3 ? "border-r border-border" : ""}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <stat.icon className="h-3 w-3 text-muted-foreground/60" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{stat.label}</span>
            </div>
            <p className="text-lg font-semibold text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Active Sessions */}
      <div className="p-4 border-b border-border">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">ACTIVE SESSIONS</p>
        <div className="space-y-1">
          {sessions.map((s, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted/20">
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${sessionDot[s.status]}`} />
              <span className="text-[12px] font-medium text-foreground w-12">{s.host}</span>
              <span className="text-[11px] text-muted-foreground flex-1">{s.platform} · {s.viewers}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${sessionStatusStyle[s.status]}`}>
                {s.status === "live" ? "Live" : "Alert"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts */}
      <div className="p-4 space-y-2">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">REAL-TIME HOST ALERTS</p>
        {alerts.map((alert, i) => (
          <div key={i} className="flex items-center gap-3 border border-border rounded-md p-3">
            <span className={`h-2 w-2 rounded-full shrink-0 ${dotColor[alert.type]}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-foreground">{alert.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badgeStyle[alert.type]}`}>
                  {alert.type === "critical" ? "Critical" : alert.type === "warning" ? "Warning" : "Resolved"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{alert.desc} · Host: <span className="font-medium text-foreground">{alert.host}</span></p>
            </div>
            <span className="text-[11px] text-muted-foreground font-mono shrink-0">{alert.time}</span>
          </div>
        ))}
      </div>

      {/* Live Transcript Feed */}
      <div className="p-4 border-t border-border space-y-1.5">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">LIVE TRANSCRIPT FEED</p>
        {[
          { time: "14:02:11", text: '"Klik keranjang kuning sekarang ya kak, diskonnya cuma hari ini."', cta: true, warning: false },
          { time: "14:02:26", text: '"Yang ini cocok untuk kulit sensitif ya."', cta: false, warning: false },
          { time: "14:03:10", text: '"Jangan lupa checkout sekarang sebelum habis."', cta: true, warning: false },
          { time: "14:05:40", text: "No CTA detected for 90 seconds.", cta: false, warning: true },
        ].map((line, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 rounded-md px-3 py-2.5 ${
              line.warning ? "bg-warning/10 border border-warning/20" : "bg-muted/30"
            }`}
          >
            <span className="text-[11px] text-muted-foreground font-mono shrink-0">[{line.time}]</span>
            <span className={`text-[12px] flex-1 ${line.warning ? "text-warning italic" : "text-foreground/80"}`}>
              {line.text}
            </span>
            {line.cta && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-success/15 text-success shrink-0">CTA</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
